import { getConfig } from "@/config";

// Base API URL
export const API_BASE_URL = getConfig().VITE_DASHBOARD_API_URL;
export const YOUZU_API_BASE_URL = getConfig().VITE_YOUZU_API_URL;


// API Endpoints
export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    SIGN_IN: `${API_BASE_URL}/auth/signin`,
    SIGN_UP: `${API_BASE_URL}/auth/signup`,
    SIGN_OUT: `${API_BASE_URL}/auth/signout`,
    RESET_PASSWORD: `${API_BASE_URL}/auth/reset-password`,
    REQUEST_RESET: `${API_BASE_URL}/auth/request-reset`,
    RESEND_VERIFICATION: `${API_BASE_URL}/api/v1/auth/resend-verification`,
    ACCOUNT_STATUS: `${API_BASE_URL}/api/v1/auth/account-status`,
  },

  // User endpoints
  USERS: {
    BASE: `${API_BASE_URL}/api/v1/users`,
    BY_ID: (id: string) => `${API_BASE_URL}/api/v1/users/${id}`,
    PROFILE: `${API_BASE_URL}/api/v1/users/profile`,
  },

  // Statistics endpoints
  STATISTICS: {
    USAGE: `${API_BASE_URL}/api/v1/statistics/usage`,
    MONTHLY: (year: number) => `${API_BASE_URL}/api/v1/statistics/monthly/${year}`,
    DAILY: (year: number, month?: number) => `${API_BASE_URL}/api/v1/statistics/daily/${year}${month ? `?month=${month}` : ''}`,
    FEATURES: `${API_BASE_URL}/api/v1/catalogue/features`,
    UNIFIED: `${API_BASE_URL}/api/v1/statistics/unified`,
    UNIFIED_MONTHLY: (year: number) => `${API_BASE_URL}/api/v1/statistics/unified/monthly/${year}`,
    POPULAR_ROOM_STYLES: (startDate: string, endDate: string, limit: number = 10) =>
      `${API_BASE_URL}/api/v1/statistics/popular-room-styles?startDate=${startDate}&endDate=${endDate}&limit=${limit}`,
    POPULAR_ROOM_TYPES: (startDate: string, endDate: string, limit: number = 10) =>
      `${API_BASE_URL}/api/v1/statistics/popular-room-types?startDate=${startDate}&endDate=${endDate}&limit=${limit}`,
    POPULAR_LENS_LABELS: (startDate: string, endDate: string, limit: number = 10) =>
      `${API_BASE_URL}/api/v1/statistics/popular-lens-search-labels?startDate=${startDate}&endDate=${endDate}&limit=${limit}`,
  },

  // Analytics endpoints
  ANALYTICS: {
    RECENT: (limit: number = 10) => `${API_BASE_URL}/api/v1/analytics/recent?limit=${limit}`,
  },

  // API Key endpoints
  API_KEYS: {
    BASE: `${API_BASE_URL}/api/v1/api-key`,
    BY_ID: (id: string) => `${API_BASE_URL}/api/v1/api-key/${id}`,
  },

  // Billing endpoints
  BILLING: {
    REVOLUT_PAY: `${API_BASE_URL}/api/v1/billing/revolut-pay`,
  },
  // Plans endpoint (public, no auth required)
  PLANS: {
    LIST: `${API_BASE_URL}/api/v1/plans`,
  },

  // Subscription endpoint
  SUBSCRIPTION: {
    CURRENT: `${API_BASE_URL}/api/v1/subscription`,
    CANCEL: `${API_BASE_URL}/api/v1/subscription`,
    CREATE: `${API_BASE_URL}/api/v1/subscription`,
  },

  // Invoice endpoints
  INVOICES: {
    LIST: (page: number = 1, limit: number = 20) =>
      `${API_BASE_URL}/api/v1/invoices?page=${page}&limit=${limit}`,
  },

  // Organization endpoints
  ORGANIZATION: {
    BASE: `${API_BASE_URL}/api/v1/organization`,
    ME: `${API_BASE_URL}/api/v1/organization/me`,
    MEMBERS: `${API_BASE_URL}/api/v1/organization/members`,
    MEMBER_BY_ID: (id: string) => `${API_BASE_URL}/api/v1/organization/members/${id}`,
    MEMBER_PERMISSIONS: (id: string) =>
      `${API_BASE_URL}/api/v1/organization/members/${id}/permissions`,
    INVITATIONS: `${API_BASE_URL}/api/v1/organization/invitations`,
    INVITATION_BY_ID: (id: string) =>
      `${API_BASE_URL}/api/v1/organization/invitations/${id}`,
    INVITATION_PUBLIC: (id: string) =>
      `${API_BASE_URL}/api/v1/organization/invitations/${id}/public`,
    INVITATION_ACCEPT: (id: string) =>
      `${API_BASE_URL}/api/v1/organization/invitations/${id}/accept`,
    AUDIT_LOGS: `${API_BASE_URL}/api/v1/organization/audit-logs`,
  },

  CATALOGUES: {
    BASE: `${API_BASE_URL}/api/v1/catalogue`,
    BY_ID: (id: string) => `${API_BASE_URL}/api/v1/catalogue/${id}`,
    FEATURES: `${API_BASE_URL}/api/v1/catalogue/features`,
    ROOM_VISUALISER_EXTRACTION: (id: string) =>
      `${API_BASE_URL}/api/v1/catalogue/${encodeURIComponent(id)}/processing-features/room-visualiser-data-extraction`,
  },

  // Support ticket endpoints
  SUPPORT: {
    TICKETS: `${API_BASE_URL}/api/v1/support/tickets`,
    TICKET_BY_ID: (id: string) => `${API_BASE_URL}/api/v1/support/tickets/${id}`,
    MESSAGES: (id: string) => `${API_BASE_URL}/api/v1/support/tickets/${id}/messages`,
  },

  // Generation endpoints
  GENERATION: {
    SWAP: (page: number = 1, limit: number = 10) =>
      `${API_BASE_URL}/api/v1/generation/swap?page=${page}&limit=${limit}`,
    ROOM_VISUALIZE: (page: number = 1, limit: number = 10) =>
      `${API_BASE_URL}/api/v1/generation/room-visualize?page=${page}&limit=${limit}`,
    TWO_D_TO_THREE_D: (page: number = 1, limit: number = 10) =>
      `${API_BASE_URL}/api/v1/generation/2d-to-3d?page=${page}&limit=${limit}`,
    MAGIC_BACKGROUND: (page: number = 1, limit: number = 10) =>
      `${API_BASE_URL}/api/v1/generation/magic-background?page=${page}&limit=${limit}`,
    PROPERTY_VISUALIZE: (
      page: number = 1,
      limit: number = 10,
      type?: "IMAGE" | "VIDEO"
    ) =>
      `${API_BASE_URL}/api/v1/generation/property-visualize?page=${page}&limit=${limit}${
        type ? `&type=${type}` : ""
      }`,
  },
};
